import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import VideoEmbed from "@/components/VideoEmbed";
import FaqList from "@/components/FaqList";
import { PATHWAYS, JOURNEY, HOME_FAQ } from "@/lib/longevity";
import { Footprints, Dumbbell, Utensils, Moon, Users, ArrowRight, Check } from "lucide-react";

// Brand imagery — candid Australian lifestyle, no clinical/biohacker visuals
const IMG_HERO      = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/1a8pqzcs_7384A00F-247C-45A0-87FA-D99F38C6B7D9%20%281%29.png";
const IMG_STRENGTH  = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/mcwntfuo_CFFAFB78-A26E-435F-9A73-2522080F9193.png";
const IMG_FAMILY    = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/3emfajue_4F91679F-CC84-4090-8476-5D1F488CB0D2.png";
const IMG_WALK      = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/c6c7e0b2dac4a045_24f1b475-2f70-454d-8fb0-3abbf583a0ed.jpg";
const IMG_COFFEE    = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/db18432874cda5bd_bc1838a7-e951-419c-8deb-6594cb64e893.jpg";
const IMG_COMMUNITY = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/9osv9r7z_1E460D03-ECF0-44DA-9346-33BBF869DECD%20%281%29.png";
const IMG_LIFESTYLE = "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/2haihr6i_1610A7C5-1FBE-4A47-9110-9BFF999A85E7.webp";

const FOUNDATIONS = [
  { key: "move",     label: "MOVE",     icon: Footprints, colour: "#E65322", copy: "Walk regularly. Keep daily movement and basic cardiovascular capacity part of normal life." },
  { key: "strength", label: "STRENGTH", icon: Dumbbell,   colour: "#3B82F6", copy: "Build and maintain muscle, strength and physical capability for the years ahead." },
  { key: "eat",      label: "EAT",      icon: Utensils,   colour: "#10B981", copy: "Build a predominantly whole-food eating pattern that supports healthy body composition and long-term health." },
  { key: "recover",  label: "RECOVER",  icon: Moon,       colour: "#7C3AED", copy: "Protect sleep, recovery and your ability to adapt to training and life stress." },
  { key: "connect",  label: "CONNECT",  icon: Users,      colour: "#F2B134", copy: "Relationships, community, purpose and social connection belong in a healthy life too." },
];

const CAPABILITIES = [
  "Carry your own bags.", "Get off the floor easily.", "Walk for kilometres without thinking about it.", "Climb stairs confidently.",
  "Maintain muscle and strength.", "Travel.", "Play with your kids now — and your grandkids later.", "Stay independent.",
  "Keep doing the things you enjoy as you get older.",
];

const BELIEFS = [
  ["Consistency over intensity", "The habits you can repeat for years matter more than short bursts of extreme effort."],
  ["Strength is health",         "Muscle and strength are assets worth protecting as you age."],
  ["Daily movement matters",     "Walking is one of the simplest ways to keep movement part of normal life."],
  ["Eat mostly real food",       "Nutrition does not need to become your full-time job."],
  ["Recovery counts",            "Training only works when your body has the opportunity to recover and adapt."],
  ["Connection matters",         "A healthy life includes relationships, community and purpose — not just workouts."],
  ["Capability is the goal",     "Looking better is valuable. Being able to keep living your life is bigger."],
];

const FOUNDER_TILES = [
  { label: "Rugby",  img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/1wfzhzah_fa4b3b96-fa2f-43cd-828b-851607d5fc47.jpg" },
  { label: "Injury", img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/rybftjxi_528f5a93-fb0e-46e9-bce1-654aa1e8f932.jpg" },
  { label: "Heavy",  img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/1gczcw6b_dc0c5608-98e0-4057-b043-5aa9c1183e6c.jpg" },
  { label: "Now",    img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_ba30-app/artifacts/91becgku_b49e5d62-b9f6-4899-b28c-b56b17a698c6.webp" },
];

const SYSTEM_PILLARS_VIDEOS = [
  { key: "walking",     label: "Walking",     icon: Footprints, colour: "#E65322", subtitle: "Daily movement foundation" },
  { key: "lifting",     label: "Lifting",     icon: Dumbbell,   colour: "#3B82F6", subtitle: "Muscle, strength, capability" },
  { key: "whole_foods", label: "Whole Foods", icon: Utensils,   colour: "#10B981", subtitle: "Nutrition quality for the long run" },
  { key: "recovery",    label: "Recovery",    icon: Moon,       colour: "#7C3AED", subtitle: "Sleep · recovery · adaptation" },
];

function Cta({ label = "START BUILDING FOR LONGER", location, dark = false, testId }) {
  return (
    <Link to="/join" data-testid={testId || `cta-${location}`} data-cta-location={location} data-event="click_join_ba30"
          className={`inline-flex items-center gap-2 font-head uppercase tracking-widest text-sm px-6 py-4 rounded-md transition-colors ${dark ? "bg-background text-foreground hover:bg-primary hover:text-primary-foreground" : "bg-primary text-primary-foreground hover:opacity-90"}`}>
      {label} <ArrowRight size={16} strokeWidth={2.5} />
    </Link>
  );
}

function OurSystemWorks() {
  const [videos, setVideos] = useState({});
  useEffect(() => { api.get("/site/videos").then((r) => setVideos(r.data)).catch(() => {}); }, []);
  return (
    <section id="our-system-works" className="bg-card border-y border-border py-24" data-testid="our-system-works">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="label-eyebrow">What credible voices say</div>
        <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
          The foundations <span className="text-primary">hold up.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-sm md:text-base text-foreground/75">
          You don't have to take our word for it. Here's what respected voices in health, strength and longevity say about the things BA30 is built around.
        </p>
        <div className="mt-14 space-y-16">
          {SYSTEM_PILLARS_VIDEOS.map((p) => {
            const list = (videos[p.key] || []).filter((v) => v.url);
            if (!list.length) return null;
            return (
              <div key={p.key} data-testid={`system-pillar-${p.key}`}>
                <div className="flex items-end gap-4 flex-wrap border-b border-border pb-4">
                  <div className="inline-flex items-center justify-center rounded-md w-11 h-11 shrink-0" style={{ backgroundColor: p.colour }}>
                    <p.icon size={22} strokeWidth={2.25} color="#fff" />
                  </div>
                  <div>
                    <div className="wordmark text-3xl md:text-4xl uppercase">{p.label}</div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{p.subtitle}</div>
                  </div>
                </div>
                <div className="mt-8 grid md:grid-cols-3 gap-6">
                  {list.map((v) => (
                    <div key={v.id} className="flex flex-col" data-testid={`system-video-${v.id}`}>
                      <VideoEmbed url={v.url} platform={v.platform} title={v.title} thumbnail={v.thumbnail} embeddable={v.embeddable} />
                      {(v.title || v.creator || v.credibility) && (
                        <div className="mt-3">
                          {v.title && <div className="font-head uppercase text-sm leading-tight">{v.title}</div>}
                          {v.creator && <div className="mt-1 text-[11px] uppercase tracking-widest" style={{ color: p.colour }}>{v.creator}</div>}
                          {v.credibility && <div className="mt-1 text-xs text-foreground/65">{v.credibility}</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = "BA30 — Built After 30 | Built for Longer";
    const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
    meta.setAttribute("name", "description");
    meta.setAttribute("content", "BA30 helps people over 30 build the strength, habits and lifestyle foundations to live healthier, stay capable and enjoy more good years. Start with the 12-week 10K30 Longevity Foundation. $59/month.");
    if (!meta.parentElement) document.head.appendChild(meta);
  }, []);

  return (
    <div data-testid="home-page">
      {/* 1. HERO — BUILT FOR LONGER */}
      <section className="bg-foreground text-background overflow-hidden" data-testid="section-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="label-eyebrow !text-primary">BA30 — Built After 30</div>
            <h1 className="font-display mt-6 text-6xl md:text-8xl uppercase leading-[0.9] tracking-tight" data-testid="hero-headline">
              Built for <span className="text-primary">longer.</span>
            </h1>
            <p className="mt-6 font-head uppercase text-lg md:text-2xl leading-snug text-background/90 max-w-2xl">
              Build the strength, habits and health to stay capable for the years ahead.
            </p>
            <div className="mt-8 max-w-2xl space-y-4 text-background/75 leading-relaxed">
              <p>BA30 helps people over 30 build the foundations for a longer, healthier and more capable life. Not through extreme fitness. Not through complicated biohacking. Through the things that matter when repeated for years:</p>
              <p className="font-head uppercase tracking-wide text-background">Move regularly. Build and maintain muscle. Eat mostly real food. Recover properly. Stay connected. Keep living your life.</p>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Cta location="hero" testId="cta-hero-primary" />
              <a href="#how-ba30-works" data-testid="cta-hero-secondary"
                 className="inline-flex items-center justify-center gap-2 border border-background/30 font-head uppercase tracking-widest text-sm px-6 py-4 rounded-md hover:bg-background hover:text-foreground transition-colors">
                SEE HOW BA30 WORKS
              </a>
            </div>
            <p className="mt-8 text-xs uppercase tracking-[0.25em] text-background/50">Stronger now. Capable for longer.</p>
          </div>
          <div className="md:col-span-5">
            <img src={IMG_HERO} alt="BA30 members walking together outdoors" fetchpriority="high"
                 className="w-full aspect-[4/5] object-cover rounded-lg" />
          </div>
        </div>
      </section>

      {/* 2. WHY THIS MATTERS AFTER 30 */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="section-why-after-30">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="label-eyebrow">Why this matters after 30</div>
            <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
              Most people don't suddenly <span className="text-primary">become unhealthy.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-14 space-y-5 text-foreground/80 leading-relaxed">
            <p>It usually happens gradually. A little less movement. A little less strength. A little more weight. Worse food. Less sleep. More stress.</p>
            <p>Life gets busy — and the basics slowly disappear. BA30 exists to help you rebuild those foundations before decline becomes your normal.</p>
            <p className="font-head uppercase text-lg leading-snug text-foreground">Your future health is shaped by <span className="text-primary">ordinary days repeated for years.</span></p>
          </div>
        </div>
      </section>

      {/* 3. HEALTHSPAN, NOT JUST LIFESPAN */}
      <section className="bg-foreground text-background" data-testid="section-healthspan">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <img src={IMG_COFFEE} alt="BA30 members sharing coffee after a walk" loading="lazy" className="w-full aspect-[4/3] object-cover rounded-lg" />
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <div className="label-eyebrow !text-primary">Healthspan, not just lifespan</div>
            <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
              Don't just live longer. <span className="text-primary">Stay capable longer.</span>
            </h2>
            <div className="mt-8 space-y-4 text-background/75 leading-relaxed max-w-2xl">
              <p>Lifespan is how long you live. Healthspan is how long you remain healthy, independent and capable.</p>
              <p>We cannot control everything about how we age. But we can influence many of the behaviours that shape how we arrive there.</p>
              <p className="font-head uppercase text-lg text-background">The goal is not simply more years. <span className="text-primary">It is more good years.</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. THE BA30 LONGEVITY FOUNDATIONS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="section-foundations">
        <div className="label-eyebrow">The BA30 longevity foundations</div>
        <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
          Five things. <span className="text-primary">Repeated for years.</span>
        </h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {FOUNDATIONS.map((f, i) => (
            <div key={f.key} className="bg-card border border-border rounded-lg p-6 lift" style={{ borderTopColor: f.colour, borderTopWidth: 3 }} data-testid={`foundation-${f.key}`}>
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-md" style={{ backgroundColor: f.colour }}><f.icon size={20} color="#fff" strokeWidth={2.25} /></div>
                <span className="text-[10px] tracking-widest text-muted-foreground">0{i + 1}</span>
              </div>
              <div className="wordmark mt-4 text-3xl uppercase">{f.label}</div>
              <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{f.copy}</p>
            </div>
          ))}
        </div>
        <p className="mt-12 font-head uppercase text-xl md:text-3xl leading-snug max-w-3xl" data-testid="foundations-close">
          Simple does not mean insignificant. <span className="text-primary">Repeated for years, the basics matter.</span>
        </p>
      </section>

      {/* 5. CAPABILITY — WHAT THIS LOOKS LIKE IN REAL LIFE */}
      <section className="bg-card border-y border-border" data-testid="section-capability">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="label-eyebrow">What this looks like in real life</div>
            <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
              What do you want your body to <span className="text-primary">still be able to do?</span>
            </h2>
            <ul className="mt-10 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="capability-list">
              {CAPABILITIES.map((c) => (
                <li key={c} className="flex items-start gap-3 text-foreground/85"><Check size={16} className="text-primary shrink-0 mt-1" strokeWidth={2.5} />{c}</li>
              ))}
            </ul>
            <p className="mt-10 font-head uppercase text-lg md:text-xl">That is the kind of fitness <span className="text-primary">BA30 is built for.</span></p>
          </div>
          <div className="md:col-span-6 grid grid-cols-2 gap-3">
            <img src={IMG_FAMILY} alt="Family dinner together" loading="lazy" className="w-full aspect-[4/5] object-cover rounded-lg" />
            <img src={IMG_WALK} alt="Walking by the water" loading="lazy" className="w-full aspect-[4/5] object-cover rounded-lg mt-8" />
          </div>
        </div>
      </section>

      {/* 6. START WITH 10K30 */}
      <section className="bg-foreground text-background" data-testid="section-start-10k30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="label-eyebrow !text-primary">Start with the foundation</div>
            <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
              10K30 — The 12-week <span className="text-primary">longevity foundation.</span>
            </h2>
            <div className="mt-8 space-y-4 text-background/75 leading-relaxed max-w-2xl">
              <p>Every new BA30 member begins with 10K30. For 12 weeks, we help you establish the behaviours your long-term health and capability can be built around.</p>
              <p>You progressively build your walking. You resistance train three times per week. You improve the way you eat. You learn how to make these behaviours fit around work, family and real life.</p>
              <p className="font-head uppercase text-lg text-background">Because before you worry about optimisation — <span className="text-primary">you need a foundation.</span></p>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Cta label="START YOUR 10K30 FOUNDATION" location="start-10k30" testId="cta-start-10k30" />
              <Link to="/10k30" data-testid="cta-explore-10k30-reset"
                    className="inline-flex items-center justify-center gap-2 border border-background/30 font-head uppercase tracking-widest text-sm px-6 py-4 rounded-md hover:bg-background hover:text-foreground transition-colors">
                HOW 10K30 WORKS
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <img src={IMG_STRENGTH} alt="Focused strength training at BA30" loading="lazy" className="w-full aspect-[4/5] object-cover rounded-lg" />
          </div>
        </div>
      </section>

      {/* 7. HOW BA30 WORKS — RESET → REBUILD → STAY BUILT */}
      <section id="how-ba30-works" className="max-w-7xl mx-auto px-6 md:px-12 py-24 scroll-mt-20" data-testid="section-how-it-works">
        <div className="label-eyebrow">How BA30 works</div>
        <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
          Reset <span className="text-primary">→</span> Rebuild <span className="text-primary">→</span> Stay Built
        </h2>
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {JOURNEY.map((j, i) => (
            <div key={j.stage} className="bg-card border border-border rounded-lg p-6" data-testid={`journey-${j.stage.toLowerCase().replace(" ", "-")}`}>
              <div className="text-[10px] tracking-widest text-muted-foreground">STAGE 0{i + 1}</div>
              <div className="wordmark mt-2 text-3xl uppercase">{j.stage}</div>
              <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{j.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 8. GOAL-BASED PATHWAYS */}
      <section className="bg-card border-y border-border" data-testid="section-pathways">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="label-eyebrow">After the foundation</div>
          <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
            Goal-based <span className="text-primary">pathways.</span>
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PATHWAYS.map((p) => (
              <div key={p.id} className="bg-background border border-border rounded-lg p-6" style={{ borderLeftColor: p.colour, borderLeftWidth: 4 }} data-testid={`pathway-${p.id.toLowerCase()}`}>
                <div className="label-eyebrow" style={{ color: p.colour }}>BA30</div>
                <div className="wordmark mt-1 text-3xl uppercase">{p.id}</div>
                <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 font-head uppercase text-xl md:text-2xl">Different goals. Same long-term objective: <span className="text-primary">stay built for longer.</span></p>
        </div>
      </section>

      {/* 9. NO BIOHACKING REQUIRED */}
      <section className="bg-foreground text-background" data-testid="section-no-biohacking">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24">
          <div className="label-eyebrow !text-primary">No biohacking required</div>
          <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
            Before you optimise anything, <span className="text-primary">build the foundations.</span>
          </h2>
          <div className="mt-8 space-y-4 text-background/75 leading-relaxed max-w-2xl">
            <p>Longevity has become complicated. Supplements. Devices. Tests. Protocols. Optimisation. Some of those tools may have a place. But none of them replace the foundations.</p>
            <p className="font-head uppercase text-lg text-background">Walk. Lift. Eat well. Sleep. Recover. Connect.</p>
            <p>Do those consistently first. Then worry about optimisation.</p>
          </div>
        </div>
      </section>

      <OurSystemWorks />

      {/* 10. WHAT BA30 BELIEVES */}
      <section id="what-ba30-believes" className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="section-believes">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="label-eyebrow">What BA30 believes</div>
            <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
              Fitness should fit your life, <span className="text-primary">not take it over.</span>
            </h2>
            <img src={IMG_COMMUNITY} alt="BA30 walk club meetup" loading="lazy" className="mt-10 w-full aspect-[4/3] object-cover rounded-lg" />
          </div>
          <div className="md:col-span-7 md:pt-14 divide-y divide-border">
            {BELIEFS.map(([h, b]) => (
              <div key={h} className="py-5 grid sm:grid-cols-12 gap-2" data-testid={`belief-${h.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="sm:col-span-5 font-head uppercase text-base">{h}</div>
                <div className="sm:col-span-7 text-sm text-foreground/70 leading-relaxed">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. FOUNDER STORY */}
      <section className="bg-card border-y border-border" data-testid="section-founder">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="label-eyebrow">Why I built BA30</div>
            <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
              I had to relearn <span className="text-primary">what fitness was for.</span>
            </h2>
            <div className="mt-8 space-y-5 text-foreground/80 leading-relaxed text-lg max-w-2xl">
              <p>After rugby, a catastrophic ankle injury changed what my body could tolerate. Years later I had gained significant weight and was still trying to train for the life I used to have.</p>
              <p>What finally worked was not more intensity. It was rebuilding the basics around the life I actually had: walking, lifting, eating mostly whole foods and doing it consistently.</p>
            </div>
            <blockquote className="mt-8 border-l-4 border-primary pl-6 max-w-2xl">
              <p className="font-head uppercase text-xl md:text-2xl leading-snug">
                BA30 grew from that experience. Not just to help people get in shape after 30 — <span className="text-primary">but to help them build a body and lifestyle they can still rely on decades from now.</span>
              </p>
            </blockquote>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-3">
            {FOUNDER_TILES.map(({ label, img }) => (
              <div key={label} className="aspect-square rounded-lg overflow-hidden border border-border" data-testid={`founder-tile-${label.toLowerCase()}`}>
                <img src={img} alt={`Troyson — ${label}`} loading="lazy" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 12. MEMBERSHIP */}
      <section className="bg-foreground text-background" data-testid="section-membership">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="label-eyebrow !text-primary">Membership</div>
            <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">
              Join BA30 — build for <span className="text-primary">the years ahead.</span>
            </h2>
            <div className="mt-8 space-y-4 text-background/75 leading-relaxed max-w-2xl">
              <p>Every member starts with the 12-week 10K30 Longevity Foundation. Build the basics first. Then choose the pathway that matches your next goal.</p>
              <p className="font-head uppercase tracking-widest text-sm text-background">LEAN · BUILD · STRONG · MAINTAIN</p>
              <p>One membership. A system designed to grow with you.</p>
            </div>
            <div className="mt-10"><Cta label="START BUILDING FOR LONGER — $59/MONTH" location="membership" testId="cta-join-ba30" /></div>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-lg border-2 border-primary bg-background/5 p-8" data-testid="home-price-card">
              <div className="label-eyebrow !text-primary">BA30 Membership</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="wordmark text-7xl leading-none" data-testid="home-price">$59</span>
                <span className="wordmark text-2xl text-background/70">/ month</span>
              </div>
              <ul className="mt-6 space-y-2 text-sm text-background/80">
                {["12-week 10K30 Longevity Foundation", "Goal pathways after Week 12 — no second checkout", "Gym + Home training tracks", "Eat Library, weekly education, Rebuild Score", "Cancel anytime"].map((t) => (
                  <li key={t} className="flex gap-2"><Check size={14} className="text-primary shrink-0 mt-0.5" strokeWidth={2.5} />{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 13. FAQ */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-24" data-testid="section-faq">
        <div className="label-eyebrow">FAQ</div>
        <h2 className="font-display mt-4 text-4xl md:text-6xl uppercase leading-[0.95]">Straight <span className="text-primary">answers.</span></h2>
        <div className="mt-10"><FaqList items={HOME_FAQ} /></div>
      </section>

      {/* 14. FINAL CTA */}
      <section className="bg-foreground text-background relative overflow-hidden" data-testid="section-final">
        <img src={IMG_LIFESTYLE} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-20" loading="lazy" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-28 text-center">
          <div className="label-eyebrow !text-primary">Stronger now. Capable for longer.</div>
          <h2 className="font-display mt-6 text-5xl md:text-7xl uppercase leading-[0.9]">
            Start building <span className="text-primary">for longer.</span>
          </h2>
          <p className="mt-6 text-background/80 max-w-xl mx-auto">Walk. Lift. Eat well. Recover. Connect. Live your life.</p>
          <div className="mt-10"><Cta location="final" testId="cta-final" /></div>
        </div>
      </section>
    </div>
  );
}
